import {resource} from '@albert-io/json-api-framework/request/builder';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {genericMandarkRequest} from 'resources/mandark.resource';

import {GlobalAssignmentsSharedOptions} from '../constants';

export const getDraftGlobalAssignmentsQuery = ({
  name = '',
  sortOption = 'updated_at',
  page = 1,
  classes = []
}) =>
  resource('draft_assignments_v1')
    .mandarkEndpoint(['draft_assignments_v1'])
    .include('teacher_v1')
    .include('classrooms_v1.teachers_v1')
    .include('question_sets_v1.questions_v3') // added to get access to question's status
    .meta({
      context: {
        assignment: {
          stats: true
        },
        default: {
          settings: true
        },
        user: {
          id: masqueradeStore.getUserIdByMasqueradeState()
        }
      }
    })
    .filter({
      teacher_v1: masqueradeStore.getUserIdByMasqueradeState()
    })
    .filter(
      {
        name: {case_insensitive_substring: name}
      },
      !!name
    )
    .page(page)
    .filter(
      {
        classrooms_v1: {
          id: classes.join(',')
        }
      },
      classes.length > 0
    )
    .sort(sortOption);

export const getPublishedGlobalAssignmentsQuery = ({
  name = '',
  sortOption = 'updated_at',
  page = 1,
  status = '',
  classes = [],
  classroomId = '',
  shared = '',
  isAdmin = false
}) =>
  resource('assignments_v3')
    .mandarkEndpoint(['assignments_v3'])
    .include('classrooms_v1.teachers_v1')
    .include('teacher_v1')
    .withMeta('assignment_v3')
    .meta({
      context: {
        assignment: {
          stats: true
        },
        // Class scope uses that class's settings + submission stats (the legacy class-list
        // semantics); the default-settings is_* meta is only meaningful cross-class.
        ...(classroomId
          ? {
              classroom: {
                id: classroomId,
                settings: true
              },
              include_submission_stats: true
            }
          : {
              default: {
                settings: true
              },
              is_open: true,
              is_scheduled: true,
              is_closed: true
            }),
        user: {
          id: masqueradeStore.getUserIdByMasqueradeState(),
          is_creator_or_classroom_teacher: true
        }
      }
    })
    .filter({
      'meta.user_is_creator_or_classroom_teacher': true
    })
    .filter({
      included: {
        classrooms_v1: {
          ...(!isAdmin
            ? {teachers_v1: masqueradeStore.getUserIdByMasqueradeState()}
            : {
                school_v5: {
                  school_personnel_v1: {
                    teacher_v1: {
                      id: masqueradeStore.getUserIdByMasqueradeState()
                    },
                    role_v1: {
                      name: {
                        not: 'teacher'
                      }
                    },
                    status: 'approved'
                  }
                }
              })
        }
      }
    })
    .filter(
      {
        teacher_v1: {
          id: masqueradeStore.getUserIdByMasqueradeState()
        }
      },
      shared === GlobalAssignmentsSharedOptions[0]
    )
    .filter(
      {
        all_of: [
          {
            classrooms_v1: {
              teachers_v1: {
                id: masqueradeStore.getUserIdByMasqueradeState()
              }
            }
          },
          {
            teacher_v1: {
              id: {
                not: masqueradeStore.getUserIdByMasqueradeState()
              }
            }
          }
        ]
      },
      shared === GlobalAssignmentsSharedOptions[1]
    )
    .filter(
      {
        name: {case_insensitive_substring: name}
      },
      !!name
    )
    .page(page)
    .filter(
      {
        'meta.is_open': true
      },
      status === 'open' && !classroomId
    )
    .filter(
      {
        'meta.is_scheduled': true
      },
      status === 'scheduled' && !classroomId
    )
    .filter(
      {
        'meta.is_closed': true
      },
      status === 'closed' && !classroomId
    )
    // Class-scoped statuses mirror the legacy class list (AssignmentsList.queries.ts):
    // submission-aware, on the class's own settings rather than the defaults.
    .filter(
      {
        all_of: [
          {
            any_of: [
              {'meta.classroom_settings_start_date': {less_than: 'now'}},
              {'meta.classroom_settings_start_date': {null: true}}
            ]
          },
          {
            any_of: [
              {'meta.classroom_settings_due_date': {greater_than: 'now'}},
              {
                'meta.classroom_settings_due_date': {less_than: 'now'},
                'meta.classroom_settings_allow_late_submissions': true
              }
            ]
          }
        ],
        'meta.count_of_incomplete_students': {not: 0}
      },
      status === 'open' && !!classroomId
    )
    .filter(
      {
        'meta.classroom_settings_start_date': {
          greater_than: 'now',
          null: false
        }
      },
      status === 'scheduled' && !!classroomId
    )
    .filter(
      {
        any_of: [
          {'meta.count_of_incomplete_students': 0},
          {
            'meta.classroom_settings_allow_late_submissions': false,
            'meta.classroom_settings_due_date': {less_than: 'now'}
          }
        ]
      },
      status === 'closed' && !!classroomId
    )
    .filter(
      {
        classrooms_v1: {
          id: classes.join(',')
        }
      },
      classes.length > 0
    )
    .filter(
      {
        classrooms_v1: classroomId
      },
      !!classroomId
    )
    .sort(sortOption);

export const getClassesQuery = ({
  includeArchived = false,
  archivedOnly = false,
  page = 1,
  isTeacher = false
}) =>
  resource('classrooms_v1')
    .mandarkEndpoint(['teachers_v1', masqueradeStore.getUserIdByMasqueradeState(), 'classrooms_v1'])
    .include('teachers_v1')
    .withMeta('classroom_v1')
    .filter(
      {
        any_of: [
          {
            status: 'active'
          },
          {
            status: 'archived'
          }
        ]
      },
      includeArchived
    )
    .filter(
      {
        status: 'active'
      },
      !includeArchived && !archivedOnly
    )
    .filter(
      {
        status: 'archived'
      },
      archivedOnly
    )
    .filter(
      {
        teachers_v1: {
          school_personnel_v1: {
            status: 'approved'
          }
        }
      },
      !isTeacher
    )
    .page(page)
    .sort('name');

export const handleDeleteAssignment = async ({assignmentId, isDraft}) => {
  const deleteQuery = {
    resourcePath: [isDraft ? 'draft_assignments_v1' : 'assignments_v3', assignmentId]
  };
  await genericMandarkRequest('delete', deleteQuery);
};
